// Free SEO tools (/tools/*): registry, localized copy loading and JSON-LD helpers.
// Copy lives in src/content/tools/<tool>.<lang>.json (English is the type source;
// every locale has a full translation, English is only a safety fallback).
import { SITE_URL, localePath, type Lang } from './i18n';

import hubEn from '../content/tools/hub.en.json';
import workoutEn from '../content/tools/workout-generator.en.json';
import timerEn from '../content/tools/hold-timer.en.json';
import plannerEn from '../content/tools/competition-planner.en.json';
import matchupEn from '../content/tools/matchup.en.json';

export type HubStrings = typeof hubEn;
export type WorkoutStrings = typeof workoutEn;
export type TimerStrings = typeof timerEn;
export type PlannerStrings = typeof plannerEn;
export type MatchupStrings = typeof matchupEn;

type JsonModules = Record<string, unknown>;
function byLang<T>(mods: JsonModules, prefix: string, en: T): (lang: Lang) => T {
  const out: Partial<Record<Lang, T>> = {};
  for (const [file, mod] of Object.entries(mods)) {
    const m = file.match(new RegExp(`${prefix}\\.([a-zA-Z-]+)\\.json$`));
    if (m) out[m[1] as Lang] = mod as T;
  }
  return (lang) => out[lang] ?? en;
}

const hub = byLang<HubStrings>(
  import.meta.glob('../content/tools/hub.*.json', { eager: true, import: 'default' }),
  'hub',
  hubEn,
);
const workout = byLang<WorkoutStrings>(
  import.meta.glob('../content/tools/workout-generator.*.json', { eager: true, import: 'default' }),
  'workout-generator',
  workoutEn,
);

const timer = byLang<TimerStrings>(
  import.meta.glob('../content/tools/hold-timer.*.json', { eager: true, import: 'default' }),
  'hold-timer',
  timerEn,
);
const planner = byLang<PlannerStrings>(
  import.meta.glob('../content/tools/competition-planner.*.json', { eager: true, import: 'default' }),
  'competition-planner',
  plannerEn,
);
const matchup = byLang<MatchupStrings>(
  import.meta.glob('../content/tools/matchup.*.json', { eager: true, import: 'default' }),
  'matchup',
  matchupEn,
);

export const hubStrings = hub;
export const workoutStrings = workout;
export const timerStrings = timer;
export const plannerStrings = planner;
export const matchupStrings = matchup;

export type ToolId = 'workout' | 'timer' | 'planner' | 'matchup';

export const TOOL_SLUGS = {
  workout: 'armwrestling-workout-generator',
  timer: 'armwrestling-hold-timer',
  planner: 'armwrestling-competition-planner',
  matchup: 'armwrestling-matchup',
} as const satisfies Record<ToolId, string>;

export const TOOLS: { id: ToolId; slug: string; emoji: string }[] = [
  { id: 'workout', slug: TOOL_SLUGS.workout, emoji: '🗓️' },
  { id: 'timer', slug: TOOL_SLUGS.timer, emoji: '⏱️' },
  { id: 'planner', slug: TOOL_SLUGS.planner, emoji: '🏆' },
  { id: 'matchup', slug: TOOL_SLUGS.matchup, emoji: '🆚' },
];

export const toolsBase = '/tools';
export const toolPath = (lang: Lang, slug: string) => localePath(lang, `${toolsBase}/${slug}`);
export const toolEmbedPath = (lang: Lang, slug: string) => localePath(lang, `${toolsBase}/${slug}/embed`);
export const toolUrl = (lang: Lang, slug: string) => `${SITE_URL}${toolPath(lang, slug)}`;

/** Replace {name} placeholders. */
export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

export function webAppLd(opts: { name: string; description: string; url: string; lang: Lang }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: opts.name,
    description: opts.description,
    url: opts.url,
    inLanguage: opts.lang,
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Any (web browser)',
    browserRequirements: 'Requires JavaScript',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'ArmProgress', url: SITE_URL, logo: `${SITE_URL}/logo.png` },
  };
}

export function faqLd(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function breadcrumbLd(crumbs: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
  };
}

export function embedSnippet(opts: { lang: Lang; slug: string; title: string; credit: string; height: number }): string {
  const src = `${SITE_URL}${toolEmbedPath(opts.lang, opts.slug)}`;
  const page = toolUrl(opts.lang, opts.slug);
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  return (
    `<iframe src="${src}" title="${esc(opts.title)}" width="100%" height="${opts.height}" style="border:0;max-width:100%" loading="lazy"></iframe>\n` +
    `<p style="font-size:13px"><a href="${page}" target="_blank" rel="noopener">${esc(opts.credit)}</a></p>`
  );
}
